import { type LiveBillResponse, LiveBillResponseSchema } from '@monorepo/shared'
import {
  type CustomerOverviewMetricsResponse,
  CustomerOverviewMetricsResponseSchema,
} from '@monorepo/shared/schemas/customers'
import { get, post, put } from '@/lib/api-client'

class CustomerDetailService {
  async getCustomerMetrics(customerId: string) {
    return get(
      `/customers/${customerId}/overview-metrics`,
      CustomerOverviewMetricsResponseSchema
    )
  }

  async getUsageData(customerId: string, days = 30) {
    const toDate = new Date().toISOString()
    const fromDate = new Date(
      Date.now() - days * 24 * 60 * 60 * 1000
    ).toISOString()
    const params = new URLSearchParams({
      customer_id: customerId,
      from_date: fromDate,
      to_date: toDate,
    })
    return get(`/customers/${customerId}/usage/daily?${params.toString()}`)
  }

  async getCurrentBill(customerId: string) {
    return this.getLiveBill(customerId)
  }

  async getContracts(customerId: string) {
    const params = new URLSearchParams({ customer_id: customerId })
    return get(`/contracts?${params.toString()}`)
  }

  async getInvoices(customerId: string) {
    const params = new URLSearchParams({ customer_id: customerId })
    return get(`/invoices?${params.toString()}`)
  }

  async getWallet(customerId: string) {
    return get(`/wallets/customer/${customerId}`)
  }

  async getAgentUsage(customerId: string) {
    return get(`/customers/${customerId}/usage`)
  }

  async getCostBreakdown(customerId: string) {
    const params = new URLSearchParams({ customer_id: customerId })
    return get(`/usage/value-receipts?${params.toString()}`)
  }

  async getTaxInfo(customerId: string) {
    return get(`/customers/${customerId}/taxes`)
  }

  async getPayments(customerId: string) {
    const params = new URLSearchParams({ customer_id: customerId })
    return get(`/payments?${params.toString()}`)
  }

  async getPaymentMethods(customerId: string) {
    const params = new URLSearchParams({ customer_id: customerId })
    return get(`/payments/methods?${params.toString()}`)
  }

  async getCustomerProfile(customerId: string) {
    return get(`/customers/${customerId}`)
  }

  async getOrganization(organizationId: string) {
    return get(`/organizations/${organizationId}`)
  }

  async updateCustomerProfile(
    customerId: string,
    profile: Record<string, unknown>
  ) {
    return put(`/customers/${customerId}`, profile)
  }

  async updateOrganization(
    organizationId: string,
    organization: Record<string, unknown>
  ) {
    return put(`/organizations/${organizationId}`, organization)
  }

  async getAvailablePlans() {
    return get('/plans')
  }

  async getPlanForContract(planId: string) {
    return get(`/plans/${planId}`)
  }

  async createContract(request: Record<string, unknown>) {
    return post('/contracts', request)
  }

  async getValueReceipts(customerId: string) {
    const params = new URLSearchParams({ customer_id: customerId })
    return get(`/usage/value-receipts?${params.toString()}`)
  }

  async getValueReceiptBreakdown(
    customerId: string,
    period: 'current' | 'monthly' | 'weekly',
    agentId?: string
  ) {
    const params = new URLSearchParams({
      customer_id: customerId,
      period,
    })
    if (agentId) params.set('agent_id', agentId)
    return get(`/usage/value-receipts?${params.toString()}`)
  }

  async getAllInvoices() {
    return get('/invoices')
  }

  async getAllPayments() {
    return get('/payments')
  }

  async createPayment(data: Record<string, unknown>) {
    return post('/payments', data)
  }

  async getLiveBill(
    customerId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<LiveBillResponse> {
    const params = new URLSearchParams({ customer_id: customerId })
    if (fromDate) params.set('from_date', fromDate)
    if (toDate) params.set('to_date', toDate)
    return get(
      `/current-bill/live?${params.toString()}`,
      LiveBillResponseSchema
    )
  }

  async getCustomerOverviewMetrics(
    customerId: string
  ): Promise<CustomerOverviewMetricsResponse> {
    return get(
      `/customers/${customerId}/overview-metrics`,
      CustomerOverviewMetricsResponseSchema
    )
  }

  async getValueReceiptsForCustomer(
    customerId: string,
    fromDate?: string,
    toDate?: string
  ) {
    const params = new URLSearchParams({ customer_id: customerId })
    if (fromDate) params.set('from_date', fromDate)
    if (toDate) params.set('to_date', toDate)
    return get(`/usage/value-receipts?${params.toString()}`)
  }

  async createInvoice(data: Record<string, unknown>) {
    return post('/invoices', data)
  }
}

export const customerDetailService = new CustomerDetailService()
